import type { orpcClient } from "@shared/lib/orpc";
import { orpc } from "@shared/lib/orpc";
import { z } from "zod";

export const PARTNER_TABS = ["retailers", "keys", "webhooks", "usage"] as const;
export type PartnerTab = (typeof PARTNER_TABS)[number];

export const partnerSearchSchema = z.object({
	/** Partner slug, for people who work for several (and platform staff). */
	partner: z.string().min(1).max(100).optional().catch(undefined),
	tab: z.enum(PARTNER_TABS).optional().catch(undefined),
	/** Retailer open in the detail sheet. */
	retailer: z.string().min(1).max(64).optional().catch(undefined),
});
export type PartnerSearch = z.infer<typeof partnerSearchSchema>;

type Client = typeof orpcClient.partner;
export type Membership = Awaited<
	ReturnType<Client["memberships"]>
>["partners"][number];
export type RetailerList = Awaited<ReturnType<Client["retailers"]["list"]>>;
export type Retailer = RetailerList["retailers"][number];
export type RetailerDetail = Awaited<ReturnType<Client["retailers"]["get"]>>;
export type OnboardingStep = NonNullable<Retailer["onboarding"]>[number];
export type RetailerStatus = Retailer["status"];
export type SourceKind = Awaited<
	ReturnType<Client["sourceKinds"]>
>["kinds"][number];
export type PartnerKey = Awaited<
	ReturnType<Client["apiKeys"]["list"]>
>["apiKeys"][number];
export type PartnerWebhooks = Awaited<ReturnType<Client["webhooks"]["list"]>>;
export type PartnerEndpoint = PartnerWebhooks["endpoints"][number];

export const membershipsQuery = () => orpc.partner.memberships.queryOptions();

export function retailersQuery(partnerId: string) {
	return orpc.partner.retailers.list.queryOptions({ input: { partnerId } });
}

/** The partner the console shows: the one in the URL, else the first. */
export function pickPartner(
	partners: readonly Membership[],
	slug: string | undefined,
): Membership | undefined {
	return partners.find((p) => p.slug === slug) ?? partners[0];
}

/**
 * Onboarding has no live channel the console can join (the retailer's own events go to its
 * admins), so a screen that shows one in progress polls fast, else at the 60 s heartbeat.
 */
export const pollWhileSettingUp = (statuses: RetailerStatus[]) =>
	statuses.includes("setting_up") ? 3_000 : 60_000;

export const STATUS_BADGE: Record<
	RetailerStatus,
	{ label: string; variant: "success" | "info" | "destructive" }
> = {
	setting_up: { label: "Setting up", variant: "info" },
	ready: { label: "Ready", variant: "success" },
	failed: { label: "Needs attention", variant: "destructive" },
};

export const STEP_LABEL: Record<OnboardingStep["step"], string> = {
	sync: "Import the catalog",
	enrich: "Learn shopper words",
	embed: "Make it searchable",
};

/** Share of the onboarding done, 0 to 100. */
export function onboardingProgress(steps: OnboardingStep[] | null): number {
	if (!steps?.length) {
		return 100;
	}
	const done = steps.filter(
		(s) => s.status === "done" || s.status === "skipped",
	).length;
	const running = steps.some((s) => s.status === "running") ? 0.5 : 0;
	return Math.round(((done + running) / steps.length) * 100);
}

export const SOURCE_LABEL: Record<SourceKind["kind"], string> = {
	SUPPY: "Suppy",
	SHOPIFY: "Shopify",
	WOOCOMMERCE: "WooCommerce",
	JSONL: "Product feed",
	PUSH: "Sent by API",
};

/** A webhook endpoint's health (partner endpoints and a retailer's own). */
export const HEALTH: Record<
	PartnerEndpoint["health"],
	{ label: string; variant: "success" | "warning" | "secondary" }
> = {
	active: { label: "Active", variant: "success" },
	failing: { label: "Failing", variant: "warning" },
	disabled: { label: "Off", variant: "secondary" },
};

export const ROLE_LABEL: Record<string, string> = {
	owner: "Owner",
	admin: "Admin",
	member: "Member",
};

export { count, usd } from "@commerce/insights/format";
